import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import LegalAcceptCheckbox from "@/components/LegalAcceptCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dedicated B2C registration. Pre-fills from the navigation state passed by:
//   • PublicRequestPage confirmation screen ("Créer mon espace client")
//   • QuoteRecoveryPage (after a successful reference lookup)
// On success we receive the JWT directly, log the user in, and route to the
// agency dashboard (which doubles as the private client dashboard since they
// share the same screens — backend filters /ride-requests by agency_id).
export default function RegisterClientPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  const prefill = location.state?.prefill || {};

  const [form, setForm] = useState({
    name: prefill.client_name || prefill.name || "",
    email: prefill.client_email || prefill.email || "",
    phone: prefill.client_phone || prefill.phone || "",
    password: "",
    password_confirm: "",
    accepted_terms: false,
  });
  const [loading, setLoading] = useState(false);

  // If we land here without prefill (someone hits the URL directly), surface
  // a small hint to use the recovery flow.
  const fromQuote = !!(prefill.client_email || prefill.client_phone || prefill.client_name);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Nom, email et mot de passe sont obligatoires"); return;
    }
    if (form.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères"); return;
    }
    if (form.password !== form.password_confirm) {
      toast.error("Les mots de passe ne correspondent pas"); return;
    }
    if (!form.accepted_terms) {
      toast.error("Vous devez accepter les conditions générales"); return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register-client`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        accepted_terms: true,
      });
      // Token comes back directly — auto-login.
      localStorage.setItem("sdrive_token", data.access_token);
      const safeUser = { ...data };
      delete safeUser.access_token;
      localStorage.setItem("sdrive_user", JSON.stringify(safeUser));
      setUser(safeUser);
      toast.success("Bienvenue ! Votre espace client est prêt.");
      navigate("/agency", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Création du compte impossible");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-client-page" className="min-h-screen bg-[#050505] flex flex-col">
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-[#121212] border border-zinc-800 rounded-xl p-6 sm:p-8">
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30">
              <UserCircle size={28} weight="fill" className="text-amber-400" />
            </span>
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white text-center mb-1">Créer mon espace client</h1>
          <p className="text-sm text-zinc-400 text-center mb-5">
            {fromQuote
              ? "Vos informations sont déjà remplies. Choisissez un mot de passe pour finaliser la création."
              : "Créez votre compte pour suivre vos demandes et consulter vos devis."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            <Field label="Nom complet" testid="reg-client-name" value={form.name} onChange={onChange("name")} placeholder="Prénom Nom" />
            <Field label="Email" testid="reg-client-email" type="email" value={form.email} onChange={onChange("email")} placeholder="vous@exemple.fr" />
            <Field label="Téléphone (optionnel)" testid="reg-client-phone" value={form.phone} onChange={onChange("phone")} placeholder="+33 6 XX XX XX XX" />
            <Field label="Mot de passe" testid="reg-client-password" type="password" value={form.password} onChange={onChange("password")} placeholder="Minimum 6 caractères" />
            <Field label="Confirmer le mot de passe" testid="reg-client-password-confirm" type="password" value={form.password_confirm} onChange={onChange("password_confirm")} />

            <LegalAcceptCheckbox
              checked={form.accepted_terms}
              onChange={(v) => setForm((p) => ({ ...p, accepted_terms: v }))}
              testid="reg-client-legal"
            />

            <Button data-testid="reg-client-submit" type="submit" disabled={loading}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11 mt-2">
              {loading ? "Création…" : <>Créer mon compte <ArrowRight size={16} className="ml-1" /></>}
            </Button>
          </form>

          <div className="mt-5 pt-4 border-t border-zinc-800 space-y-2 text-center">
            <button data-testid="reg-client-have-account" onClick={() => navigate("/login")}
              className="text-xs text-zinc-400 hover:text-white block mx-auto">
              J'ai déjà un compte → Se connecter
            </button>
            <button data-testid="reg-client-recover" onClick={() => navigate("/quote-recovery")}
              className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
              <MagnifyingGlass size={12} /> Retrouver une demande avec un numéro de devis
            </button>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-zinc-400 text-xs uppercase tracking-wider">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
    </div>
  );
}
